import { useLanguage } from "../i18n/LanguageContext";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_NUMBER, INSTAGRAM_URL, FACEBOOK_URL, EMAIL, MAPS_URL, MAP_EMBED_URL } from "../content/siteContent";
import { Reveal, Overline } from "./Reveal";
import { IconPin, IconPhone, IconWhatsApp, IconInstagram, IconFacebook, IconMail, IconArrow } from "./Icons";

const ContactSection = () => {
    const { t } = useLanguage();
    const cards = [
        {
            id: "address",
            icon: IconPin,
            label: t.contact.addressLabel,
            value: t.contact.address,
            href: MAPS_URL,
            action: t.contact.mapLink,
        },
        {
            id: "phone",
            icon: IconPhone,
            label: t.contact.phoneLabel,
            value: PHONE_DISPLAY,
            href: `tel:${PHONE_TEL}`,
            action: t.header.callUs,
            ltrValue: true,
        },
        {
            id: "whatsapp",
            icon: IconWhatsApp,
            label: t.contact.whatsappLabel,
            value: "+962 79 591 4652",
            href: `https://wa.me/${WHATSAPP_NUMBER}`,
            action: t.hero.ctaPrimary,
            ltrValue: true,
        },
        {
            id: "instagram",
            icon: IconInstagram,
            label: t.contact.instagramLabel,
            value: t.contact.instagramText,
            href: INSTAGRAM_URL,
            action: t.contact.instagramLabel,
        },
        {
            id: "facebook",
            icon: IconFacebook,
            label: t.contact.facebookLabel,
            value: t.contact.facebookText,
            href: FACEBOOK_URL,
            action: t.contact.facebookLabel,
        },
        {
            id: "email",
            icon: IconMail,
            label: t.contact.emailLabel,
            value: EMAIL,
            href: `mailto:${EMAIL}`,
            action: t.contact.emailLabel,
            ltrValue: true,
        },
    ];
    return (
        <section id="contact" className="scroll-mt-24 py-12 md:py-20" aria-labelledby="contact-title" data-testid="contact-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal className="max-w-2xl">
                    <Overline>{t.contact.overline}</Overline>
                    <h2 id="contact-title" className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        {t.contact.title}
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-4 sm:grid-cols-2 md:mt-16 md:gap-6 lg:grid-cols-3">
                    {cards.map((c, i) => (
                        <Reveal key={c.id} delay={Math.min(i * 0.06, 0.3)}>
                            <a
                                href={c.href}
                                target={c.href.startsWith("http") ? "_blank" : undefined}
                                rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                                data-testid={`contact-card-${c.id}`}
                                className="group flex h-full flex-col rounded-2xl border border-ink/10 bg-surface p-6 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-gold-500/50 hover:shadow-[0_24px_45px_-25px_rgba(0,77,77,0.4)]"
                            >
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold-100 text-gold-600 transition-[transform,background-color,color] duration-300 group-hover:scale-110 group-hover:bg-gold-500 group-hover:text-ink">
                                    <c.icon className="h-5 w-5" />
                                </span>
                                <span className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-gold-600">{c.label}</span>
                                <span dir={c.ltrValue ? "ltr" : undefined} className="mt-2 text-base font-medium leading-relaxed text-ink">
                                    {c.value}
                                </span>
                                <span className="mt-auto inline-flex items-center gap-2 pt-4 text-sm font-semibold text-pine transition-colors duration-300 group-hover:text-gold-600">
                                    {c.action}
                                    <IconArrow className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1 rtl:group-hover:translate-x-1 rtl:group-hover:-translate-x-0 ltr:group-hover:translate-x-1" />
                                </span>
                            </a>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.1} className="mt-6 md:mt-8">
                    <div className="relative overflow-hidden rounded-[2rem] border border-ink/10 shadow-[0_30px_60px_-35px_rgba(0,77,77,0.4)]">
                        <iframe
                            src={MAP_EMBED_URL}
                            title={t.contact.mapFrameTitle}
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                            className="h-[320px] w-full border-0 md:h-[420px]"
                            data-testid="contact-map-iframe"
                        />
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default ContactSection;
